import { forwardRef, useImperativeHandle, useRef } from 'react'

import { Input } from 'antd'
import { type TextAreaProps } from 'antd/es/input'
import { type TextAreaRef } from 'antd/es/input/TextArea'

import {
  createCustomShowCountFormatter,
  handleBlur,
  handleChange,
  handleKeyDown,
  handlePaste,
} from '../../lib/self-validate-fields'

const { TextArea } = Input

type AutoTextAreaProps = Omit<TextAreaProps, 'onChange'> & {
  blockClipboard?: boolean
  value: string
  onChange: (value: string) => void
  autoSize?: { minRows?: number; maxRows?: number }
  correctOnKeyDown?: boolean
}

export type SelfValidateTextAreaRef = {
  focus: () => void
  getSelection: () => { start: number; end: number }
  setSelection: (pos: number) => void
}

export const SelfValidateTextArea = forwardRef<
  SelfValidateTextAreaRef,
  AutoTextAreaProps
>(
  (
    {
      blockClipboard = false,
      value,
      onChange,
      autoSize = { minRows: 2, maxRows: 8 },
      maxLength,
      correctOnKeyDown = true,
      ...rest
    },
    ref,
  ) => {
    const textAreaRef = useRef<TextAreaRef>(null)
    const stringValue = Array.isArray(value) ? value.join(', ') : value

    useImperativeHandle(ref, () => ({
      focus: () => {
        textAreaRef.current?.focus()
      },
      getSelection: () => {
        const el = textAreaRef.current?.resizableTextArea?.textArea
        return {
          start: el?.selectionStart ?? stringValue.length,
          end: el?.selectionEnd ?? stringValue.length,
        }
      },
      setSelection: (pos: number) => {
        const el = textAreaRef.current?.resizableTextArea?.textArea
        if (el) {
          el.selectionStart = el.selectionEnd = pos
        }
      },
    }))

    const isError = maxLength && stringValue.length > maxLength

    return (
      <TextArea
        ref={textAreaRef}
        value={stringValue}
        autoSize={autoSize}
        maxLength={undefined}
        showCount={
          typeof rest.showCount === 'boolean' && rest.showCount
            ? createCustomShowCountFormatter(maxLength)
            : typeof rest.showCount === 'boolean' && !rest.showCount
              ? undefined
              : rest.showCount
        }
        status={isError ? 'error' : undefined}
        {...rest}
        onChange={(e) => {
          handleChange(e, onChange)
        }}
        onKeyDown={(e) => {
          if (!correctOnKeyDown) return
          handleKeyDown(e, stringValue)
          rest.onKeyDown?.(e)
        }}
        onPaste={(e) => {
          handlePaste(e, stringValue, maxLength, blockClipboard, onChange)
          rest.onPaste?.(e)
        }}
        onBlur={(e) => {
          handleBlur(stringValue, onChange)
          rest.onBlur?.(e)
        }}
      />
    )
  },
)

SelfValidateTextArea.displayName = 'SelfValidateTextArea'
